import type { ComponentType } from 'react';

import type { LucideProps } from '@brightlocal/icons';

import type { FeatureFlagKey } from 'shared/consts';

interface BaseNavItem {
  /** Stable identifier used for React keys, test hooks, and analytics. */
  id: string;
  /** i18n key resolved with `t()` to produce the label shown in the sidebar. */
  labelKey: string;
  /** Icon glyph rendered to the left of the label. Typed to match `@brightlocal/icons` components. */
  icon: ComponentType<LucideProps>;
  /**
   * Optional i18n key of a small promotional badge (e.g. "New") rendered after the label.
   * Omit for regular items; remove from an item once its feature is no longer new.
   */
  badgeLabelKey?: string;
  /**
   * Optional feature-flag gate. When provided, the item is only shown when the flag evaluates to `true`.
   * Combined AND-gate with `visibleWhen`: both must pass when both are set.
   */
  featureFlag?: FeatureFlagKey;
  /**
   * Optional route predicate. When provided, the item is only shown when the current pathname satisfies
   * the predicate. Useful for contextual nav items scoped to a specific route subtree.
   */
  visibleWhen?: (pathname: string) => boolean;
}

/**
 * Discriminant values for the nav item variants.
 *
 * TEMPORARY: `OLD_PLATFORM` only exists while some pages still live on the Old Platform and we
 * redirect users there. Once every page has a New Platform equivalent, Old Platform redirection is
 * no longer needed — this variant (and `OldPlatformNavItem`) can be removed, leaving `ROUTER` and
 * `COLLAPSIBLE` as the only nav item kinds.
 */
export const NAV_ITEM_TYPE = {
  ROUTER: 'router',
  OLD_PLATFORM: 'old-platform',
  COLLAPSIBLE: 'collapsible'
} as const;

/**
 * A nav item that links to a New Platform (TanStack Router) route.
 */
export interface RouterNavItem extends BaseNavItem {
  /** Discriminant type for the router nav item variant */
  type: typeof NAV_ITEM_TYPE.ROUTER;
  /** Static target path. Use values from `shared/routes` to keep paths consistent. */
  to: string;
  /**
   * Dynamic path builder for parameterized routes. When provided, the sidebar resolves the link target
   * by calling this with the current route params instead of using the static `to` value.
   */
  toFn?: (params: Record<string, string>) => string;
  /**
   * Builds the Old Platform path the item opens while `featureFlag` is off, so it stands in for the
   * Old Platform page it replaces instead of disappearing. Omit to hide the item while the flag is off.
   */
  flagOffOldPlatformPathFn?: (params: Record<string, string>) => string;
}

/**
 * A nav item that opens an Old Platform page in a new tab via `redirectToOldPlatform`.
 */
export interface OldPlatformNavItem extends BaseNavItem {
  /** Discriminant type for the old platform nav item variant */
  type: typeof NAV_ITEM_TYPE.OLD_PLATFORM;
  /** Builds the Old Platform path from the given route params (e.g. `{ id }` on location pages). */
  oldPlatformPathFn: (params: Record<string, string>) => string;
}

/** The link part of a router item, shared by top-level items and sub-items. */
export type RouterNavLink = Omit<RouterNavItem, 'icon' | 'badgeLabelKey'>;

/** A router row inside a collapsible group. */
export type RouterNavSubItem = RouterNavLink;

/**
 * A nested item rendered inside a collapsible group. Same link shapes as a top-level item,
 * but without an icon or badge — sub-items render as plain text rows.
 */
export type NavSubItem = RouterNavSubItem | Omit<OldPlatformNavItem, 'icon' | 'badgeLabelKey'>;

/**
 * A collapsible group of sub-items (e.g. Citations → Citation Builder).
 * The trigger row toggles the group open and closed; it is not a link itself.
 */
export interface CollapsibleNavItem extends Omit<BaseNavItem, 'badgeLabelKey'> {
  /** Discriminant type for the collapsible nav item variant */
  type: typeof NAV_ITEM_TYPE.COLLAPSIBLE;
  /** Sub-items in display order. Each is gated individually; a group with no visible sub-items is hidden. */
  items: readonly NavSubItem[];
}

/**
 * Discriminated union of all sidebar nav item shapes.
 *
 * Items without a gate are always visible.
 * Items with `featureFlag` are only shown when the flag is on. Plan access is not a sidebar gate:
 * the tool page explains why a tool can't be used.
 * Items with `visibleWhen` are only shown when the predicate returns `true` for the current pathname.
 */
export type NavItem = RouterNavItem | OldPlatformNavItem | CollapsibleNavItem;

/**
 * A group of nav items rendered as one sidebar section, divided from adjacent groups by a separator.
 */
export interface NavGroup {
  /** Stable identifier for the group — used for React keys and the group's menu `dataHook`. */
  id: string;
  /** Items in display order. Each item is still gated/filtered individually at render time. */
  items: readonly NavItem[];
}
